'use client';

import { c as _c } from "react/compiler-runtime";
import { type ActiveWallet, createTransactionTraceId, createTxError, type QueryClient, reconcileProposalEntity, type SigningDeps, type SubmissionResult, TxErrorCode, type useTurnkeySubmissionDeps } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { D6_SCALE } from '@/config/solana';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import { type DynamicTranslator, resolveApiErrorMessage } from '@/lib/errors/resolve-api-error';
import type { Network } from '@/lib/network';
import { withDemoMode } from '@/lib/network';
import { buildProposalBackShareUrl, openShareModal } from '@/lib/share';
import { logger } from '@/lib/telemetry/logger';
import { type BackingAccounts, backMarketAndSubmit, trackBackTxException, trackBackTxFailure } from '@/lib/tx-engine/back-market';
import type { useSubmitTransaction } from '@/lib/tx-engine/use-submit-transaction';
import { confettiOriginFromElement, fireTradeBurst } from '@/lib/ui/confetti';
import { dismissToast, showToast } from '@/lib/ui/toast';
import { useInvalidateWalletBalances } from '@/queries/wallet-balance';
type SubmitTxFn = ReturnType<typeof useSubmitTransaction>;
type Translator = ReturnType<typeof useTranslations>;
interface FundHandlerDeps {
  activeWallet: ActiveWallet;
  proposalPubkey: string;
  /** Indexed market bindings; undefined falls back to the builder's Market RPC read. */
  backingAccounts: BackingAccounts | undefined;
  usdcAmount: number;
  fundStakeOutcomeIndex: number;
  network: Network;
  /** Form root — confetti bursts from here on success (not the screen center). */
  formRootRef: React.RefObject<HTMLDivElement | null>;
  submitTx: SubmitTxFn;
  signingDeps: SigningDeps;
  turnkeyDeps: ReturnType<typeof useTurnkeySubmissionDeps>;
  queryClient: QueryClient;
  invalidateWalletBalances: () => void;
  fundingControllers: Map<AbortController, string>;
  formatBalance: (value: number) => string;
  /** False while the proposal hands off to its market: the graduation celebration owns the moment. */
  shouldCelebrate: () => boolean;
  tToasts: Translator;
  tBack: Translator;
  tRoot: DynamicTranslator;
}
export function useProposalFund() {
  const $ = _c(5);
  const invalidateWalletBalances = useInvalidateWalletBalances();
  const tToasts = useTranslations("launchpad.toasts");
  const tBack = useTranslations("launchpad.back");
  const tRoot = useTranslations() as unknown as DynamicTranslator;
  let t0;
  if ($[0] !== invalidateWalletBalances || $[1] !== tBack || $[2] !== tRoot || $[3] !== tToasts) {
    t0 = deps => runFund({
      ...deps,
      invalidateWalletBalances,
      tToasts,
      tBack,
      tRoot
    });
    $[0] = invalidateWalletBalances;
    $[1] = tBack;
    $[2] = tRoot;
    $[3] = tToasts;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  return t0;
}
async function runFund(deps: FundHandlerDeps): Promise<SubmissionResult> {
  const {
    activeWallet,
    proposalPubkey,
    backingAccounts,
    usdcAmount,
    fundStakeOutcomeIndex,
    network,
    formRootRef,
    submitTx,
    signingDeps,
    turnkeyDeps,
    queryClient,
    invalidateWalletBalances,
    fundingControllers,
    formatBalance,
    shouldCelebrate,
    tToasts,
    tBack,
    tRoot
  } = deps;
  const abortController = new AbortController();
  fundingControllers.set(abortController, `${network}:${activeWallet.address}:${proposalPubkey}`);
  const traceId = createTransactionTraceId();
  // Non-blocking, but not silent: `no_observer` / `error` RESOLVE, so only an
  // outcome check reports a reconcile that never reached the backend.
  const reconcileAndReport = (): void => {
    void reconcileProposalEntity(queryClient, proposalPubkey).then(outcome => {
      if (outcome !== 'success') {
        logger.warn('proposal_back.cache_reconciliation_failed', {
          traceId,
          proposalId: proposalPubkey,
          outcome
        });
      }
    }).catch(() => {
      logger.warn('proposal_back.cache_reconciliation_failed', {
        traceId,
        proposalId: proposalPubkey,
        outcome: 'rejected'
      });
    });
  };
  const errorContext = {
    traceId,
    activeWallet,
    marketPubkey: proposalPubkey,
    outcomeIndex: fundStakeOutcomeIndex,
    network,
    startedAt: Date.now()
  };
  if (backingAccounts === undefined) {
    logger.warn('proposal_back.indexed_accounts_unavailable', {
      traceId,
      proposalId: proposalPubkey
    });
  }
  const submittingToastId = `${TOAST_IDS.TX_SUBMITTING}_${traceId}`;
  const successToastId = `${TOAST_IDS.PROPOSAL_BACKED}_${traceId}`;
  const errorToastId = `${TOAST_IDS.PROPOSAL_BACK_ERROR}_${traceId}`;
  let cancelledByUser = false;
  let submissionStarted = false;
  let submissionResult: SubmissionResult | undefined;
  showToast({
    id: submittingToastId,
    message: tToasts('backingSubmitting'),
    variant: 'loading',
    duration: Infinity,
    cancel: {
      label: tBack('cancel'),
      onClick: (): void => {
        cancelledByUser = true;
        abortController.abort();
      }
    }
  });
  try {
    const amountD6Int = Math.floor(usdcAmount * D6_SCALE);
    // `back` is now market-level — no per-outcome stake instruction.
    // The UI still tracks `fundStakeOutcomeIndex` for analytics + share
    // URLs because that's the outcome the user was conceptually backing.
    const result = await backMarketAndSubmit({
      submitTx: (...args) => {
        submissionStarted = true;
        return submitTx(...args);
      },
      signingDeps,
      turnkeyDeps,
      activeWallet: {
        id: activeWallet.id,
        address: activeWallet.address,
        type: activeWallet.type
      },
      traceId,
      signal: abortController.signal
    }, {
      marketPubkey: proposalPubkey,
      accounts: backingAccounts,
      amountD6: amountD6Int
    });
    submissionResult = result;
    if (result.ok) {
      // Celebrate a successful backing the same way market detail celebrates a
      // confirmed trade — burst from the form, not the screen center. The form
      // stays mounted through submit, so read the origin at fire time.
      if (shouldCelebrate()) fireTradeBurst(confettiOriginFromElement(formRootRef.current));
      onEvent('proposal_backed', {
        traceId,
        proposalId: proposalPubkey,
        outcomeIndex: fundStakeOutcomeIndex,
        amount: usdcAmount
      });
      invalidateWalletBalances();
      // Reconcile the canonical proposal entity to backend truth. The chip /
      // Withdraw-tab `canWithdraw` read `viewerShareBalance` off
      // `useGetProposal`; its realtime `backing.v0` patch lands post-index
      // (the `back` tx confirms on chain BEFORE the indexer publishes), so
      // without this the freshly-funded stake stays invisible until a hard
      // refresh (MOR-658). Withdraw logs the same condition through its
      // `allSettled` branch.
      reconcileAndReport();
      dismissToast(submittingToastId);
      // Capture share context locally before the toast unmounts —
      // activeWallet may change before the user taps Share.
      const userPubkeyStr = activeWallet.address;
      const amountD6Str = String(amountD6Int);
      const outcomeIndexNum = fundStakeOutcomeIndex;
      const formattedAmount = formatBalance(usdcAmount);
      showToast({
        id: successToastId,
        message: tToasts('backingSuccess', {
          amount: formattedAmount
        }),
        variant: 'success',
        duration: 5000,
        // Sonner renders `action` reliably as a standalone primary
        // button; `cancel` only renders predictably when paired with
        // an `action`. This toast has a single CTA so we use `action`.
        action: {
          label: tToasts('shareBacked'),
          onClick: (): void => {
            const sharePath = buildProposalBackShareUrl(proposalPubkey, {
              userPubkey: userPubkeyStr,
              amountD6: amountD6Str,
              outcomeIndex: outcomeIndexNum,
              displayAmount: formattedAmount
            });
            const origin = typeof window !== 'undefined' ? window.location.origin : '';
            const shareUrl = ((): string => {
              if (origin.length === 0) return '';
              const u = new URL(sharePath, origin);
              withDemoMode(u, network === 'mainnet' ? 'mainnet' : 'devnet');
              return u.toString();
            })();
            openShareModal({
              surface: {
                kind: 'proposal_back',
                proposalPubkey,
                userPubkey: userPubkeyStr,
                amountD6: amountD6Str,
                outcomeIndex: outcomeIndexNum,
                displayAmount: formattedAmount
              },
              url: shareUrl,
              text: tToasts('shareTweetText', {
                amount: formattedAmount
              }),
              title: tToasts('shareModalTitle')
            });
          }
        }
      });
    } else if (abortController.signal.aborted) {
      dismissToast(submittingToastId);
      if (cancelledByUser) {
        showToast({
          id: `${TOAST_IDS.TX_CANCELLED}_${traceId}`,
          message: tToasts('backingCancelled'),
          variant: 'warning',
          duration: 5000
        });
      }
    } else {
      trackBackTxFailure('back', errorContext, result.error);
      // The program only rejects a back with InvalidMarketState once the market
      // has left Backing (the final back opened trading), so the form is
      // rendering a proposal the backend has not caught up on yet — pull it
      // forward instead of leaving the Back CTA armed for another rejection.
      if (result.error.morfiError === 'InvalidMarketState') {
        reconcileAndReport();
      }
      dismissToast(submittingToastId);
      showToast({
        id: errorToastId,
        message: resolveApiErrorMessage(result.error, tRoot) ?? tToasts('backingError'),
        variant: 'error',
        duration: Infinity,
        cancel: {
          label: tBack('close'),
          onClick: (): void => {
            dismissToast(errorToastId);
          }
        }
      });
    }
    return result;
  } catch (err: unknown) {
    if (abortController.signal.aborted) {
      dismissToast(submittingToastId);
      return submissionResult ?? {
        ok: false,
        traceId,
        error: createTxError(submissionStarted ? TxErrorCode.TX_UNKNOWN : TxErrorCode.TX_BUILD_FAILED, traceId, 'building')
      };
    }
    logger.error('proposal_back.exception', {
      traceId,
      errorClass: err instanceof Error ? 'Error' : typeof err
    });
    if (submissionResult === undefined) {
      trackBackTxException('back', errorContext, err, submissionStarted);
    }
    dismissToast(submittingToastId);
    showToast({
      id: errorToastId,
      message: resolveApiErrorMessage(err, tRoot) ?? tToasts('backingError'),
      variant: 'error',
      duration: Infinity,
      cancel: {
        label: tBack('close'),
        onClick: (): void => {
          dismissToast(errorToastId);
        }
      }
    });
    return submissionResult ?? {
      ok: false,
      traceId,
      error: createTxError(submissionStarted ? TxErrorCode.TX_UNKNOWN : TxErrorCode.TX_BUILD_FAILED, traceId, 'building')
    };
  } finally {
    fundingControllers.delete(abortController);
  }
}